import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  effect,
  signal,
  untracked,
} from '@angular/core';
import { form } from '@angular/forms/signals';
import { Router } from '@angular/router';

import {
  HelpDocumentDto,
  WalkthroughDocumentStepDto,
  isNavStepPath,
} from '@ups/xplat/base/in-app-help';
import { BaseComponent } from '@ups/xplat/core';

import { WrenWayfindingService } from '../../walkthrough-wren/wren-wayfinding.service';

/** What the selected list row edits: the tour's metadata, or one step by index. */
const METADATA = -1;

/**
 * WI 35852 S3b: the signal-form model for the selected row. The metadata row and a
 * step carry the same three fields, so one form serves whichever row is selected.
 */
export interface WalkthroughRowModel {
  path: string | null;
  title: string | null;
  html: string | null;
}

type EditedRow = Pick<HelpDocumentDto, 'path' | 'title' | 'html'>;

/** Writes `value` unless the row already holds it; null and undefined count as equal,
 * so a row the author only looked at keeps its absent keys. */
function writeField(
  row: EditedRow,
  key: keyof EditedRow,
  value: string | null
) {
  if ((row[key] ?? null) !== value) {
    row[key] = value!;
  }
}

/**
 * Production walkthrough authoring: a two-pane editor over the tour's step list.
 *
 * Left pane lists the tour (metadata row + every step) with per-step kind
 * (element vs navigation, discriminated by `isNavStepPath` — the same rule the
 * player runs), an on-this-page resolve indicator, reorder, and delete. Right
 * pane edits the selected row: element steps pick their anchor from the live
 * DOM (help-element-picker) and can test-fly the wren to it; navigation steps
 * take an absolute route (leading "/" enforced, since that IS the nav-step
 * discriminator).
 *
 * All edits mutate the parent editor's deep-cloned `document`, so nothing
 * touches the store until the author saves — closing the editor discards
 * everything, which is why per-step deletes need no confirm. The selected row's
 * path, title and body are a signal form (`model` / `f`, WI 35852 S3b): selecting
 * a row seeds the model from it, and an effect writes every model change back
 * into that row of the clone. Path changes made by code (kind flip, nav-path
 * normalising, "use current page", the page-specific toggle) go through the
 * model too, so the model is the only writer of the selected row.
 *
 * OnPush: every edit is a template event in this view, so it marks the view. The
 * one input that changes without an event here is the page DOM behind the editor
 * (the "off-page" badge reads it live), so a MutationObserver mirrors the set of
 * `help-path` anchors into `anchorsTick`, which `resolvesHere` reads.
 */
@Component({
  selector: 'help-editor-walkthrough',
  templateUrl: './editor-walkthrough.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorWalkthroughComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() document: HelpDocumentDto;

  @Output() complete = new EventEmitter<void>();

  /** Selected list row: METADATA (-1) or a step index. */
  selected = METADATA;

  /** Inline feedback for the last wren test-fly, cleared on selection change. */
  lastTest: { index: number; found: boolean } | null = null;

  readonly metadataRow = METADATA;

  readonly model = signal<WalkthroughRowModel>({
    path: null,
    title: null,
    html: null,
  });
  readonly f = form(this.model);

  /** Writes each model change into the selected row of the parent's clone. */
  private readonly writeBack = effect(() => {
    const m = this.model();
    untracked(() => {
      const row = this.selectedRow();
      if (row) {
        writeField(row, 'path', m.path);
        writeField(row, 'title', m.title);
        writeField(row, 'html', m.html);
      }
    });
  });

  /** Bumped when the set of `help-path` anchors on the page changes. */
  private readonly anchorsTick = signal(0);
  private anchorPaths = new Set<string>();
  private anchorObserver?: MutationObserver;

  constructor(
    private router: Router,
    private wayfinding: WrenWayfindingService,
    private ngZone: NgZone
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['document']) {
      this.selected = METADATA;
      this.lastTest = null;
      this.seed();
    }
  }

  ngOnInit(): void {
    if (
      typeof document === 'undefined' ||
      typeof MutationObserver === 'undefined'
    ) {
      return;
    }
    this.anchorPaths = this.readAnchorPaths();
    // Outside the zone: a document-wide observer must not tick the whole app per
    // mutation batch. It only touches a signal, and only when the anchor set changed.
    this.ngZone.runOutsideAngular(() => {
      this.anchorObserver = new MutationObserver(() => this.syncAnchors());
      this.anchorObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['help-path'],
      });
    });
  }

  override ngOnDestroy(): void {
    this.anchorObserver?.disconnect();
    super.ngOnDestroy();
  }

  get steps(): WalkthroughDocumentStepDto[] {
    this.document.steps = this.document.steps || [];
    return this.document.steps;
  }

  /** Step indexes whose path is empty — neither playable nor classifiable.
   * Complete is blocked while any exist. */
  get invalidSteps(): number[] {
    return this.steps
      .map((s, i) => (s.path?.trim() ? -1 : i))
      .filter((i) => i >= 0);
  }

  /** Element steps whose anchor is NOT in the current DOM. Informational, never
   * blocking — a cross-page tour legitimately references other pages' anchors. */
  get unresolvedHereCount(): number {
    return this.steps.filter(
      (s) => this.isElementStep(s) && !!s.path?.trim() && !this.resolvesHere(s)
    ).length;
  }

  isNavStep(step: WalkthroughDocumentStepDto): boolean {
    return isNavStepPath(step?.path);
  }

  /** Element step = has a path that isn't a route. An empty path previews as an
   * element step (the common authoring start: add, then pick). */
  isElementStep(step: WalkthroughDocumentStepDto): boolean {
    return !this.isNavStep(step);
  }

  /** Whether an element step's anchor is rendered on the page under the editor.
   * Attribute-value match, not a CSS selector — help-paths contain slashes. */
  resolvesHere(step: WalkthroughDocumentStepDto): boolean {
    this.anchorsTick(); // dependency only: re-read when the page's anchors change
    const path = step?.path?.trim();
    if (!path || isNavStepPath(path) || typeof document === 'undefined') {
      return false;
    }
    return Array.from(document.querySelectorAll('[help-path]')).some(
      (e) => e.getAttribute('help-path') === path
    );
  }

  select(index: number): void {
    this.selected = index;
    this.lastTest = null;
    this.seed();
  }

  /** Append a step of the given kind and select it. A nav step starts on the
   * page the author is looking at; an element step starts empty for the picker. */
  addStep(kind: 'element' | 'nav'): void {
    const steps = this.steps;
    steps.push({
      index: steps.length,
      path: kind === 'nav' ? this.currentRoute() : '',
      title: '',
      html: '',
    });
    this.select(steps.length - 1);
  }

  removeStep(index: number): void {
    const steps = this.steps;
    if (index < 0 || index >= steps.length) {
      return;
    }
    steps.splice(index, 1);
    this.reindex();
    if (this.selected >= steps.length) {
      this.select(steps.length ? steps.length - 1 : METADATA);
    } else {
      // The same index may now hold a different step.
      this.seed();
    }
  }

  /** Swap a step with its neighbor. Selection follows the moved step so the
   * author can keep nudging it without re-selecting. */
  move(index: number, delta: -1 | 1): void {
    const steps = this.steps;
    const target = index + delta;
    if (
      index < 0 ||
      index >= steps.length ||
      target < 0 ||
      target >= steps.length
    ) {
      return;
    }
    [steps[index], steps[target]] = [steps[target], steps[index]];
    this.reindex();
    if (this.selected === index) {
      this.select(target);
    } else if (this.selected === target) {
      this.select(index);
    }
  }

  /**
   * Flip the selected step's kind. The path resets to the kind's natural start
   * value (current route / empty-for-picker) because an element help-path is
   * meaningless as a route and vice versa.
   */
  setKind(step: WalkthroughDocumentStepDto, kind: 'element' | 'nav'): void {
    if (kind === 'nav' && !this.isNavStep(step)) {
      this.writePath(step, this.currentRoute());
    } else if (kind === 'element' && !this.isElementStep(step)) {
      this.writePath(step, '');
    }
  }

  /** Nav-path writes go through here so the leading "/" (the nav-step
   * discriminator the player keys on) can never be dropped by hand-editing. */
  setNavPath(step: WalkthroughDocumentStepDto, value: string): void {
    const trimmed = (value ?? '').trim();
    this.writePath(
      step,
      trimmed.startsWith('/') ? trimmed : `/${trimmed.replace(/^\/+/, '')}`
    );
  }

  /** The route box is hand-bound: every keystroke is normalised by setNavPath. */
  onNavPathInput(step: WalkthroughDocumentStepDto, event: Event): void {
    this.setNavPath(step, (event.target as HTMLInputElement).value);
  }

  /** Stamp the page under the editor as the selected nav step's route. */
  useCurrentPage(step: WalkthroughDocumentStepDto): void {
    this.writePath(step, this.currentRoute());
  }

  /** Fly the wren to an element step's anchor as a live preview. A miss is the
   * authoring signal that the picked path doesn't resolve on this page. */
  testFly(step: WalkthroughDocumentStepDto, index: number): void {
    const found = this.wayfinding.flyTo(step.path, step.title || undefined);
    this.lastTest = { index, found };
  }

  /** Legacy metadata toggle: a page-specific tour anchors to its page URL. */
  updatePathBasedOnCheckbox(state: boolean): void {
    this.writePath(this.document, state ? this.currentRoute() : null);
  }

  onComplete(): void {
    if (!this.invalidSteps.length) {
      this.complete.emit();
    }
  }

  private readAnchorPaths(): Set<string> {
    return new Set(
      Array.from(document.querySelectorAll('[help-path]')).map((e) =>
        e.getAttribute('help-path')!
      )
    );
  }

  private syncAnchors(): void {
    const next = this.readAnchorPaths();
    const same =
      next.size === this.anchorPaths.size &&
      Array.from(next).every((p) => this.anchorPaths.has(p));
    if (!same) {
      this.anchorPaths = next;
      this.anchorsTick.update((n) => n + 1);
    }
  }

  private currentRoute(): string {
    return this.router?.routerState?.snapshot?.url || '/';
  }

  /** The row the form edits: the tour itself, or the selected step. */
  private selectedRow(): EditedRow | undefined {
    if (!this.document) {
      return undefined;
    }
    return this.selected === METADATA
      ? this.document
      : this.steps[this.selected];
  }

  /** Loads the selected row into the form. */
  private seed(): void {
    const row = this.selectedRow();
    this.model.set({
      path: row?.path ?? null,
      title: row?.title ?? null,
      html: row?.html ?? null,
    });
  }

  /**
   * The step body. kendo-editor is hand-bound, not [formField]: a signal form echoes writeValue
   * back after the editor's own change, and Kendo then rebuilds the document from a stale value,
   * throwing the caret to the end on every keystroke (PR 5765 review, code-bd).
   */
  setHtml(html: string): void {
    this.model.update((m) => ({ ...m, html }));
  }

  /** A selected row's path changes through the model (the effect writes it back);
   * any other row is written directly. */
  private writePath(row: EditedRow, path: string | null): void {
    if (row && row === this.selectedRow()) {
      this.model.update((m) => ({ ...m, path }));
    } else if (row) {
      row.path = path!;
    }
  }

  private reindex(): void {
    this.steps.forEach((s, i) => (s.index = i));
  }
}
